import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  signOut,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  updateUserAttributes,
} from 'aws-amplify/auth'
import { MIGRATION_LOGIN } from '@/config/constants'
import { COGNITO_ATTRIBUTES } from './cognito-config'
import { friendlyLoginError } from './login-error'
import type { AuthSession, AuthUser, IAuthService, PasswordResetData, RegisterData } from './types'

/**
 * Build an AuthSession from a given Amplify session, or fetch a fresh one
 * if `session` isn't supplied. Passing a session in lets callers that
 * already have one (e.g. `refreshSession`) avoid a redundant
 * `fetchAuthSession` round-trip.
 */
async function buildAuthSession(
  session?: Awaited<ReturnType<typeof fetchAuthSession>>,
): Promise<AuthSession> {
  const resolved = session ?? (await fetchAuthSession())
  const idToken = resolved.tokens?.idToken
  const accessToken = resolved.tokens?.accessToken
  if (!idToken || !accessToken) {
    throw new Error('No valid tokens in session')
  }

  const payload = idToken.payload
  // edgeUserId is a hard invariant — Cognito stamps custom:edge_user_id at
  // signup/migration (+ backfill). Absent = a provisioning bug; fail loud at this
  // boundary rather than let a session run without a stable id.
  const edgeUserId = payload[COGNITO_ATTRIBUTES.edgeUserId] as string | undefined
  if (!edgeUserId) {
    throw new Error('Session is missing edgeUserId (custom:edge_user_id not stamped)')
  }
  const user: AuthUser = {
    email: payload['email'] as string,
    sub: payload['sub'] as string,
    edgeUserId,
    givenName: (payload[COGNITO_ATTRIBUTES.givenName] as string) ?? '',
    familyName: (payload[COGNITO_ATTRIBUTES.familyName] as string) ?? '',
    betaAccess: payload[COGNITO_ATTRIBUTES.betaAccess] === 'true',
    migrationStage: (payload[COGNITO_ATTRIBUTES.migrationStage] as string) || undefined,
  }

  return {
    tokens: {
      accessToken: accessToken.toString(),
      idToken: idToken.toString(),
      refreshToken: '',
    },
    user,
  }
}

/**
 * Real Cognito auth service for production.
 * Uses AWS Amplify v6 modular auth APIs.
 */
export class CognitoAuthService implements IAuthService {
  private registeredPassword: string | null = null
  /**
   * Shared in-flight refresh. Every concurrent caller (proactive timer,
   * base-query 401 retry, etc.) awaits the same promise so we only ever
   * issue a single `fetchAuthSession({ forceRefresh: true })` call.
   */
  private refreshInFlight: Promise<AuthSession | null> | null = null

  // --- Registration ---

  async register(data: RegisterData): Promise<void> {
    this.registeredPassword = data.password
    await signUp({
      username: data.email.trim().toLowerCase(),
      password: data.password,
      options: {
        userAttributes: {
          email: data.email.trim().toLowerCase(),
          [COGNITO_ATTRIBUTES.givenName]: data.givenName,
          [COGNITO_ATTRIBUTES.familyName]: data.familyName,
        },
      },
    })
  }

  async confirmRegistration(email: string, code: string): Promise<void> {
    await confirmSignUp({ username: email.trim().toLowerCase(), confirmationCode: code })
    // Auto-login after confirmation if we have the registered password
    if (this.registeredPassword) {
      // Sign out any residual session from signUp before logging in
      try {
        await signOut()
      } catch {
        /* ignore */
      }
      await this.login(email, this.registeredPassword)
      this.registeredPassword = null
    }
  }

  async resendVerificationCode(email: string): Promise<void> {
    await resendSignUpCode({ username: email.trim().toLowerCase() })
  }

  // --- Login / Logout ---

  async login(email: string, password: string): Promise<AuthSession> {
    // [auth-diag] A FULL sign-in — this is the ONLY frontend path that fires the Cognito
    // PostAuthentication trigger (and its AdminUserGlobalSignOut). Silent refreshes do NOT.
    // If these lines appear ~every 15 min, something is re-logging-in on a timer.
    console.info('[auth-diag] FULL LOGIN (signIn) start', email, new Date().toISOString())
    // Migration requires USER_PASSWORD_AUTH so Cognito can verify the legacy password.
    // Normalize before calling Cognito: migration response email must match the request
    // username even though the pools support case-insensitive sign-in.
    // Cognito frames a UserMigration trigger throw as "UserMigration failed with
    // error <msg>." — normalize it here, at the Cognito boundary, so the store can
    // keep passing `.message` straight through to the UI.
    const attemptSignIn = () =>
      signIn({
        username: email.trim().toLowerCase(),
        password,
        ...(MIGRATION_LOGIN ? { options: { authFlowType: 'USER_PASSWORD_AUTH' as const } } : {}),
      })
    let signInResult: Awaited<ReturnType<typeof signIn>>
    try {
      try {
        signInResult = await attemptSignIn()
      } catch (err) {
        // Stale tokens left in storage make Amplify refuse a new sign-in. Clear them
        // locally and retry once; a second failure falls through to the normal mapping.
        if ((err as { name?: string })?.name !== 'UserAlreadyAuthenticatedException') throw err
        try {
          await signOut()
        } catch {
          /* ignore */
        }
        signInResult = await attemptSignIn()
      }
    } catch (err) {
      // Keep the Cognito name (analytics reads it); the message is the friendly copy.
      const name = (err as { name?: string })?.name
      const friendly = new Error(friendlyLoginError(err))
      if (name) friendly.name = name
      if (name === 'PasswordResetRequiredException') {
        ;(friendly as Error & { code: string }).code = 'RESET_PASSWORD'
      }
      throw friendly
    }
    const { nextStep } = signInResult
    if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
      const err = new Error('Please verify your email before signing in.')
      ;(err as Error & { code: string }).code = 'CONFIRM_SIGN_UP'
      throw err
    }
    if (nextStep.signInStep !== 'DONE') {
      const step = nextStep.signInStep as string
      const resetRequired =
        step === 'RESET_PASSWORD' || step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      const err = new Error(
        resetRequired
          ? 'Please reset your password to continue.'
          : /TOTP|MFA|SMS|EMAIL_CODE/.test(step)
            ? 'Two-step verification is not supported here yet. Contact support.'
            : 'Sign-in could not be completed. Contact support.',
      )
      err.name = `SignInStep:${step}`
      if (resetRequired) (err as Error & { code: string }).code = 'RESET_PASSWORD'
      throw err
    }
    const authSession = await buildAuthSession()
    console.info('[auth-diag] FULL LOGIN done', email, new Date().toISOString())
    return authSession
  }

  async logout(): Promise<void> {
    await signOut()
  }

  // --- Session ---

  async getSession(): Promise<AuthSession | null> {
    try {
      return await buildAuthSession()
    } catch (err) {
      // [auth-diag] No valid session could be built (tokens gone/revoked) => logged out.
      console.warn('[auth-diag] getSession: no valid session', (err as { name?: string })?.name)
      return null
    }
  }

  async refreshSession(): Promise<AuthSession | null> {
    if (this.refreshInFlight) return this.refreshInFlight
    this.refreshInFlight = (async () => {
      // [auth-diag] This is the REFRESH_TOKEN flow — it does NOT fire PostAuthentication.
      console.info('[auth-diag] refreshSession start (forceRefresh)', new Date().toISOString())
      try {
        const session = await fetchAuthSession({ forceRefresh: true })
        if (!session.tokens?.idToken || !session.tokens?.accessToken) {
          console.warn('[auth-diag] refreshSession: no tokens returned')
          return null
        }
        const at = session.tokens.accessToken.payload
        const exp = Number(at?.exp)
        const iat = Number(at?.iat)
        // Defensive: must never throw (mock tokens may lack exp/iat) — a throw here would be
        // swallowed by the catch and wrongly report the refresh as failed.
        const expInfo = Number.isFinite(exp)
          ? `exp=${exp} ttl=${Number.isFinite(iat) ? `${exp - iat}s` : '?'} ` +
            `(expires ${new Date(exp * 1000).toISOString()})`
          : 'exp=unknown'
        console.info(`[auth-diag] refreshSession OK; accessToken iat=${at?.iat} ${expInfo}`)
        return await buildAuthSession(session)
      } catch (err) {
        // The error NAME is the diagnosis. "NotAuthorizedException: Refresh Token has been
        // revoked" => a GlobalSignOut killed the refresh token (self-revoke race OR a second
        // login). A wording about "expired" => natural refresh-token-lifetime expiry instead.
        const e = err as { name?: string; message?: string }
        console.warn(
          `[auth-diag] refreshSession FAILED name=${e?.name} message="${e?.message}" ` +
            `=> session is dead, app will bounce to login`,
        )
        return null
      }
    })().finally(() => {
      this.refreshInFlight = null
    })
    return this.refreshInFlight
  }

  // --- Password reset ---

  async sendPasswordResetCode(email: string): Promise<void> {
    await resetPassword({ username: email.trim().toLowerCase() })
  }

  async submitPasswordReset(data: PasswordResetData): Promise<void> {
    await confirmResetPassword({
      username: data.email.trim().toLowerCase(),
      confirmationCode: data.verificationCode,
      newPassword: data.newPassword,
    })
  }

  // --- Password ---

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await updatePassword({ oldPassword, newPassword })
  }

  // --- Profile ---

  async updateAttributes(attributes: Record<string, string>): Promise<void> {
    const userAttributes: Record<string, string> = {}
    for (const [key, value] of Object.entries(attributes)) {
      userAttributes[key] = value
    }
    await updateUserAttributes({ userAttributes })
  }
}
